import { useMemo, useState, type ReactNode } from 'react';
import {
  AROMA_FAMILIES,
  MAX_AROMA_TAGS,
  type AromaFamily,
  type ReferenceTermView,
} from '@cercle/shared';
import { Input, Muted, Tag } from '../ui';
import { AromaWheel } from './AromaWheel';
import { useReference } from './useReference';
import styles from './aroma-picker.module.scss';

/**
 * Le choix des descripteurs, à la roue.
 *
 * On amène la famille sous le repère, on tape ses descripteurs, on tourne, on en tape
 * d'autres. Les six familles ne sont plus empilées : elles se succèdent sous le doigt, et
 * l'écran de note retrouve une hauteur qu'on peut tenir d'un pouce.
 *
 * **Le récapitulatif n'est pas une décoration.** Dès qu'on tourne, les descripteurs choisis
 * dans les familles précédentes quittent l'écran ; sans une ligne qui les garde tous
 * visibles, un descripteur posé puis masqué par la rotation deviendrait impossible à
 * retirer. C'est aussi lui qui montre le total grossir au fil des familles.
 *
 * Les descripteurs viennent du référentiel et non d'une constante compilée : c'est ce qui
 * permet d'en ajouter un en pleine séance. Les **familles**, elles, restent les six du
 * contrat partagé — la roue a six secteurs de soixante degrés, une septième n'aurait nulle
 * part où se poser.
 *
 * Un terme créé rejoint la famille sous le repère. C'est pourquoi le champ d'ajout vit
 * dans la famille ouverte et non au-dessus de toutes : « Miel » ne veut rien dire tant
 * qu'on n'a pas dit s'il est boisé ou fruité.
 */
export interface AromaPickerProps {
  selected: readonly string[];
  onToggle: (tag: string) => void;
}

const FAMILIES = Object.keys(AROMA_FAMILIES) as AromaFamily[];

export function AromaPicker({ selected, onToggle }: AromaPickerProps): ReactNode {
  const { terms, loading, add } = useReference('AROMA');
  const [index, setIndex] = useState(0);
  const [adding, setAdding] = useState(false);

  const byFamily = useMemo(() => {
    const map = new Map<string, ReferenceTermView[]>(FAMILIES.map((family) => [family, []]));
    for (const term of terms) {
      // Un descripteur dont la famille aurait disparu du contrat n'est pas perdu : il
      // rejoint « Technique », plutôt que de devenir invisible et impossible à retirer.
      const family = term.family && map.has(term.family) ? term.family : 'Technique';
      map.get(family)?.push(term);
    }
    // Le plus employé en tête : c'est le vocabulaire vivant du cercle.
    for (const list of map.values()) list.sort((a, b) => b.usageCount - a.usageCount);
    return map;
  }, [terms]);

  // `FAMILIES` n'est jamais vide — les six familles sont dans le contrat — mais rien dans
  // le type ne le dit, et un index hors bornes doit retomber quelque part de sensé.
  const family = FAMILIES[index] ?? 'Technique';
  const current = byFamily.get(family) ?? [];
  const chosenHere = current.filter((term) => selected.includes(term.label)).length;
  const full = selected.length >= MAX_AROMA_TAGS;

  return (
    <div className={styles.picker}>
      {/*
        Le récapitulatif, toutes familles confondues, dans l'ordre où les descripteurs ont
        été choisis. C'est la seule surface qui permet d'en retirer un sans revenir sur sa
        famille — et la seule qui montre le total. Il précède la roue : la consigne d'usage
        qu'il porte doit être lue avant qu'on touche à la roue, pas après.
      */}
      <section className={styles.chosen}>
        <header className={styles.chosenHead}>
          <span>Vos descripteurs</span>
          {/*
            La zone vive porte sur le seul compteur. L'annoncer sur la liste entière ferait
            relire les huit descripteurs à chaque appui, alors que les pastilles disent déjà
            « sélectionné » par `aria-pressed` : ce qu'il reste à savoir, c'est le total.
          */}
          <span aria-live="polite">
            {selected.length} / {MAX_AROMA_TAGS}
          </span>
        </header>

        {selected.length === 0 ? (
          <Muted>Tournez la roue, puis touchez les descripteurs qui vous viennent.</Muted>
        ) : (
          <div className={styles.tags}>
            {selected.map((tag) => (
              <Tag key={tag} gold selected onClick={() => onToggle(tag)}>
                {tag} ×
              </Tag>
            ))}
          </div>
        )}
      </section>

      <AromaWheel
        families={FAMILIES}
        index={index}
        onChange={(next) => {
          setIndex(next);
          // Un champ d'ajout ouvert appartient à la famille qu'on quitte : le laisser
          // ouvert créerait « Miel » dans la famille suivante, sans qu'on l'ait voulu.
          setAdding(false);
        }}
        caption={`${chosenHere} choisi${chosenHere > 1 ? 's' : ''} · ${current.length} descripteur${current.length > 1 ? 's' : ''}`}
      />

      {/* Pas de zone vive ici : la roue annonce déjà la famille par son `aria-valuetext`. */}
      <section className={styles.family}>
        <h3 className={styles.familyName}>{family}</h3>

        {loading && terms.length === 0 ? <Muted>Chargement des descripteurs…</Muted> : null}

        <div className={styles.tags}>
          {current.map((term) => (
            <Tag
              key={term.id}
              selected={selected.includes(term.label)}
              // Une pastille qui ne peut plus rien ajouter le dit, plutôt que de se laisser
              // toucher sans effet — `useTastingForm` compte là-dessus pour ignorer
              // silencieusement le neuvième descripteur.
              disabled={full && !selected.includes(term.label)}
              onClick={() => onToggle(term.label)}
            >
              {term.label}
            </Tag>
          ))}

          {adding ? (
            <AddTerm
              family={family}
              onAdd={async (label) => {
                const term = await add(label, family);
                setAdding(false);
                if (!selected.includes(term.label)) onToggle(term.label);
              }}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <Tag dashed disabled={full} onClick={() => setAdding(true)}>
              + Autre
            </Tag>
          )}
        </div>
      </section>

      {full ? <Muted>Huit descripteurs au plus — au-delà, la note ne décrit plus rien.</Muted> : null}
    </div>
  );
}

function AddTerm({
  family,
  onAdd,
  onCancel,
}: {
  family: string;
  onAdd: (label: string) => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (label.trim().length < 2) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(label.trim());
    } catch {
      setError('Impossible d’ajouter ce descripteur hors-ligne.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className={styles.addTerm}>
      <Input
        autoFocus
        value={label}
        placeholder={`Descripteur ${family.toLowerCase()}`}
        onChange={(event) => setLabel(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void submit();
          }
          if (event.key === 'Escape') onCancel();
        }}
      />
      <Tag dashed disabled={busy || label.trim().length < 2} onClick={() => void submit()}>
        {busy ? 'Ajout…' : 'Ajouter'}
      </Tag>
      {error ? <Muted>{error}</Muted> : null}
    </span>
  );
}
